/**
 * 单项目能源快照装配（只读）。
 *
 * 输出结构与 nandu-energy-screen/data.js 的 createDemoSnapshot 保持一致，
 * 供 ?project= 子屏直接渲染；独立路由 /group/nandu-energy-screen 仍使用演示快照。
 */
import {
  buildProjectRange,
  queryProjectEnergy,
  queryTodayUse,
  queryDailyElectricity,
  queryWater,
  queryDeviceStatistics,
  queryBills,
  queryRetain,
  queryAirDevices,
  queryMeters,
  queryWaterMeters,
  querySolarMonth
} from './project-api'

function num (value) {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function round (value, digits = 2) {
  const parsed = num(value)
  if (parsed == null) return null
  const factor = 10 ** digits
  return Math.round(parsed * factor) / factor
}

function percentage (part, total) {
  const p = num(part)
  const t = num(total)
  if (p == null || t == null || t <= 0) return null
  return round(p / t * 100, 1)
}

function pad (value) {
  return String(value).padStart(2, '0')
}

function timeLabel (date) {
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function shortDate (dateString) {
  const value = String(dateString || '')
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value.slice(5, 7)}.${value.slice(8, 10)}` : value
}

async function settle (task) {
  try {
    return { ok: true, value: await task() }
  } catch (error) {
    return { ok: false, error: error && error.message ? error.message : '请求失败' }
  }
}

function meterRecords (rows) {
  return (rows || []).map(row => ({
    id: `M-${row.id}`,
    name: String(row.meterName || row.serial || row.id),
    type: 'electric',
    typeName: '电表',
    status: Number(row.onlineStatus) === 1 ? 'online' : 'offline',
    updatedAt: String(row.updateTime || row.syncTime || '').slice(5, 16) || '—'
  }))
}

function waterRecords (rows) {
  return (rows || []).map(row => ({
    id: `W-${row.id}`,
    name: String(row.waterName || row.serial || row.id),
    type: 'water',
    typeName: '水表',
    status: Number(row.onlineStatus) === 1 ? 'online' : 'offline',
    updatedAt: String(row.updateTime || row.upTime || '').slice(5, 16) || '—'
  }))
}

function airRecords (rows) {
  return (rows || []).map(row => {
    const offline = Number(row.state) === 1 || row.faultCode === 'LOST'
    const running = !offline && Number(row.powerStatus) === 1
    return {
      id: `A-${row.id}`,
      name: String(row.name || row.serial || row.id),
      type: 'ac',
      typeName: '空调内机',
      status: offline ? 'offline' : (running ? 'online' : 'standby'),
      updatedAt: String(row.updateTime || row.lastOfflineTime || '').slice(5, 16) || '—'
    }
  })
}

function buildEvents (records) {
  return records
    .filter(record => record.status === 'offline')
    .slice(0, 5)
    .map((record, index) => ({
      id: `LIVE-${record.id}`,
      time: record.updatedAt && record.updatedAt.includes(' ') ? record.updatedAt.slice(-5) : '—',
      title: `${record.name}离线`,
      detail: `${record.typeName}通信状态未知，建议核查采集链路与网关供电。`,
      severity: '重要',
      status: '待排查',
      deviceType: record.type,
      projectName: '',
      order: index
    }))
}

/**
 * @param {object} project 项目注册信息（id/name/shortName/hasPv）
 * @param {{ now?: Date }} options
 */
export async function buildProjectSnapshot (project, { now = new Date() } = {}) {
  const managerId = Number(project.id)
  const range = buildProjectRange(now)

  const [energyRes, todayTenantRes, todayPublicRes, dailyRes, waterMonthRes, waterTodayRes, deviceRes, billRes, retainRes, airRes, meterRes, waterMeterRes] = await Promise.all([
    settle(() => queryProjectEnergy(managerId, range)),
    settle(() => queryTodayUse(managerId, 0)),
    settle(() => queryTodayUse(managerId, 1)),
    settle(() => queryDailyElectricity(managerId)),
    settle(() => queryWater(managerId, range.monthStart, range.today)),
    settle(() => queryWater(managerId, range.today, range.today)),
    settle(() => queryDeviceStatistics(managerId, range.today)),
    settle(() => queryBills(managerId, range)),
    settle(() => queryRetain(managerId)),
    settle(() => queryAirDevices(managerId)),
    settle(() => queryMeters(managerId)),
    settle(() => queryWaterMeters(managerId))
  ])

  const critical = []
  if (!energyRes.ok) critical.push('本月用电')
  if (!dailyRes.ok) critical.push('近7日用电')
  if (!deviceRes.ok) critical.push('设备统计')
  if (!billRes.ok) critical.push('本月账单')
  if (!airRes.ok) critical.push('空调设备')
  if (critical.length) {
    const detail = [energyRes, dailyRes, deviceRes, billRes, airRes].filter(item => !item.ok).map(item => item.error).filter(Boolean).join('；')
    throw new Error(`${critical.join('、')}获取失败${detail ? '：' + detail : ''}`)
  }

  const energy = energyRes.value
  const devices = deviceRes.value
  const bills = billRes.value
  const tenant = energy.tenantGeneral + energy.tenantAir
  const common = energy.commonGeneral + energy.commonAir
  const total = tenant + common

  // 近 7 日：只取完整日（昨天及以前），按日期升序；无记录时按 0 展示并标记为部分数据。
  const daily = dailyRes.value
    .filter(row => row.date >= range.trendStart && row.date <= range.trendEnd)
    .sort((a, b) => a.date.localeCompare(b.date))
  const trendEmpty = !daily.length
  const trend = (trendEmpty
    ? Array.from({ length: 7 }, (_, index) => {
      const date = new Date(`${range.trendStart}T00:00:00`)
      date.setDate(date.getDate() + index)
      return { date: `${pad(date.getMonth() + 1)}/${pad(date.getDate())}`, value: 0 }
    })
    : daily.map(row => ({ date: row.date.replace(/-/g, '/').slice(5), value: round(row.value, 1) }))
  )

  const airRows = airRes.value
  if (airRows.length !== devices.air.total) {
    throw new Error('空调列表与设备统计数量不一致，暂不汇总运行状态')
  }
  const airOnline = airRows.filter(row => Number(row.state) !== 1 && row.faultCode !== 'LOST')
  const airRunning = Math.min(airOnline.filter(row => Number(row.powerStatus) === 1).length, Math.max(0, devices.air.total - devices.air.offline))
  const airStandby = Math.max(0, devices.air.total - devices.air.offline - airRunning)

  // 光伏：仅接入南都电站；失败时明确标记不可用，不用 0 冒充发电量。
  let solar = { generated: 0, self: 0, exported: 0, available: false }
  let solarError = ''
  if (project.hasPv) {
    try {
      const month = range.today.slice(0, 7)
      const value = await querySolarMonth(month)
      solar = { generated: round(value.generated, 2), self: value.self == null ? null : round(value.self, 2), exported: value.exported == null ? null : round(value.exported, 2), available: true }
      if (solar.self == null || solar.exported == null) {
        solarError = '光伏发电与自用/上网未通过校验'
        solar.available = false
      }
    } catch (error) {
      solarError = error && error.message ? error.message : '光伏数据获取失败'
      solar = { generated: 0, self: 0, exported: 0, available: false }
    }
  }

  const waterMonth = waterMonthRes.ok ? waterMonthRes.value : null
  const waterToday = waterTodayRes.ok ? waterTodayRes.value : null
  const retain = retainRes.ok ? retainRes.value : null

  const serviceBill = Math.max(0, bills.bill - bills.elecBill - bills.waterBill)
  const servicePay = Math.max(0, bills.pay - bills.elecPay - bills.waterPay)
  const categories = [
    { name: '电费', receivable: round(bills.elecBill, 2), received: round(bills.elecPay, 2) },
    { name: '水费', receivable: round(bills.waterBill, 2), received: round(bills.waterPay, 2) },
    { name: '服务费及其他', receivable: round(serviceBill, 2), received: round(servicePay, 2) }
  ]

  const airList = airRecords(airRes.value)
  const meterList = meterRecords(meterRes.ok ? meterRes.value : [])
  const waterList = waterRecords(waterMeterRes.ok ? waterMeterRes.value : [])
  const records = [
    ...airList,
    ...meterList,
    ...waterList
  ]
  const events = buildEvents([...airList, ...meterList, ...waterList])

  const failedSources = []
  if (trendEmpty) failedSources.push('近7日用电（暂无记录）')
  if (!todayTenantRes.ok || !todayPublicRes.ok) failedSources.push('今日用电')
  if (!waterMonthRes.ok) failedSources.push('本月用水')
  if (!waterTodayRes.ok) failedSources.push('今日用水')
  if (!retainRes.ok) failedSources.push('留存金额')
  if (!meterRes.ok) failedSources.push('电表明细')
  if (!waterMeterRes.ok) failedSources.push('水表明细')
  if (project.hasPv && !solar.available) failedSources.push('光伏')

  return {
    mode: failedSources.length ? 'partial' : 'live',
    updatedAt: timeLabel(now),
    period: `${range.monthStart.slice(0, 4)}.${shortDate(range.monthStart)} — ${shortDate(range.today)}`,
    sources: {
      failed: failedSources,
      solar: project.hasPv ? (solar.available ? 'ready' : 'error') : 'none',
      records: meterRes.ok && waterMeterRes.ok ? 'ready' : 'partial'
    },
    energy: {
      total: round(total, 2),
      tenant: round(tenant, 2),
      common: round(common, 2),
      gridImport: round(total - (solar.available && solar.self != null ? solar.self : 0), 2),
      tenantToday: todayTenantRes.ok ? round(todayTenantRes.value, 2) : null,
      commonToday: todayPublicRes.ok ? round(todayPublicRes.value, 2) : null,
      projectToday: todayTenantRes.ok && todayPublicRes.ok ? round(todayTenantRes.value + todayPublicRes.value, 2) : null,
      waterToday: waterToday ? round(waterToday.total, 2) : null,
      tenantWater: waterMonth ? round(waterMonth.tenant, 2) : null,
      indoor: round(energy.tenantGeneral, 2),
      airConditioning: round(energy.tenantAir, 2),
      indoorPercent: percentage(energy.tenantGeneral, tenant) || 0,
      airConditioningPercent: percentage(energy.tenantAir, tenant) || 0
    },
    trend,
    photovoltaic: {
      generated: solar.generated,
      selfUse: solar.self,
      exported: solar.exported,
      selfUsePercent: solar.available ? percentage(solar.self, solar.generated) : null,
      available: solar.available,
      error: solarError,
      zones: []
    },
    devices: {
      total: devices.electric.total + devices.water.total + devices.air.total,
      online: devices.electric.total - devices.electric.offline + devices.water.total - devices.water.offline + devices.air.total - devices.air.offline,
      offline: devices.electric.offline + devices.water.offline + devices.air.offline,
      onlinePercent: percentage(
        devices.electric.total - devices.electric.offline + devices.water.total - devices.water.offline + devices.air.total - devices.air.offline,
        devices.electric.total + devices.water.total + devices.air.total
      ),
      types: [
        { key: 'electric', name: '电表', icon: 'flash', total: devices.electric.total, online: devices.electric.total - devices.electric.offline, offline: devices.electric.offline },
        { key: 'water', name: '水表', icon: 'water', total: devices.water.total, online: devices.water.total - devices.water.offline, offline: devices.water.offline },
        { key: 'ac', name: '空调内机', icon: 'fan', total: devices.air.total, online: devices.air.total - devices.air.offline, offline: devices.air.offline }
      ],
      airConditioning: {
        total: devices.air.total,
        running: airRunning,
        standby: airStandby,
        offline: devices.air.offline
      }
    },
    deviceRecords: records,
    events,
    finance: {
      scope: 'prepaid',
      receivable: round(bills.bill, 2),
      received: round(bills.pay, 2),
      outstanding: round(bills.bill - bills.pay, 2),
      collectionPercent: percentage(bills.pay, bills.bill),
      balance: retain == null ? null : round(retain, 2),
      categories
    }
  }
}
